import { Link } from 'react-router-dom';
import React, { useEffect, useRef, useState } from 'react';
import { Container, Group, Button, Badge, Title, Text, Stack, Grid, Card, List } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { IconClock, IconUsers, IconCalendar, IconBolt, IconStar, IconArrowRight, IconCircleCheck } from '@tabler/icons-react';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';

/* ============ SEO + Calendly helpers (JS only) ============ */
function setOrCreateMeta(select, content) {
  const selector = select.name ? `meta[name="${select.name}"]` : `meta[property="${select.property}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    if (select.name) el.setAttribute('name', select.name);
    if (select.property) el.setAttribute('property', select.property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function ensureLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function injectJsonLd(obj, id = 'seo-jsonld') {
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    document.head.appendChild(script);
  }
  script.text = JSON.stringify(obj);
}

const CALENDLY_URL = 'https://calendly.com/adrai-marc/quart-intro-call';

function useCalendly() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!document.querySelector('link[data-calendly="css"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://assets.calendly.com/assets/external/widget.css';
      link.setAttribute('data-calendly', 'css');
      document.head.appendChild(link);
    }
    const existing = document.querySelector('script[data-calendly="js"]');
    if (existing && window.Calendly) {
      setReady(true);
      return;
    }
    if (!existing) {
      const s = document.createElement('script');
      s.src = 'https://assets.calendly.com/assets/external/widget.js';
      s.async = true;
      s.defer = true;
      s.setAttribute('data-calendly', 'js');
      s.onload = () => setReady(true);
      document.body.appendChild(s);
    } else {
      const iv = setInterval(() => {
        if (window.Calendly) {
          setReady(true);
          clearInterval(iv);
        }
      }, 150);
      return () => clearInterval(iv);
    }
  }, []);

  const open = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (window.Calendly && typeof window.Calendly.initPopupWidget === 'function') {
      window.Calendly.initPopupWidget({ url: CALENDLY_URL });
    } else {
      window.open(CALENDLY_URL, '_blank', 'noopener,noreferrer');
    }
    return false;
  };

  return { ready, open };
}

// Three.js background for Hero via CDN (no package install)
function useThreeHero() {
  const mountRef = useRef(null);
  useEffect(() => {
    let cleanup = () => {};
    const init = () => {
      const THREE = window.THREE;
      if (!THREE || !mountRef.current) return;
      const mount = mountRef.current;
      const width = mount.clientWidth || 600;
      const height = mount.clientHeight || 320;
      // Create overlay container for canvas (absolute, behind content)
      const overlay = document.createElement('div');
      overlay.style.position = 'absolute';
      overlay.style.inset = '0';
      overlay.style.pointerEvents = 'none';
      overlay.style.zIndex = '0';
      mount.appendChild(overlay);
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
      camera.position.z = 5.5;
      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setSize(width, height);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.domElement.style.position = 'absolute';
      renderer.domElement.style.inset = '0';
      overlay.appendChild(renderer.domElement);
      // Subtle background blobs
      const group = new THREE.Group();
      const makeBlob = (color, scale, pos) => {
        const g = new THREE.IcosahedronGeometry(1.1, 2);
        const m = new THREE.MeshStandardMaterial({ color, metalness: 0.4, roughness: 0.8, transparent: true, opacity: 0.18 });
        const mesh = new THREE.Mesh(g, m);
        mesh.scale.setScalar(scale);
        mesh.position.set(pos[0], pos[1], pos[2]);
        return mesh;
      };
      const b1 = makeBlob(0x1c7ed6, 1.2, [-1.2, 0.2, 0]);
      const b2 = makeBlob(0x748ffc, 1.0, [1.4, -0.3, -0.2]);
      const b3 = makeBlob(0x82c0ff, 0.9, [0.2, 0.8, -0.4]);
      group.add(b1, b2, b3);
      scene.add(group);
      const key = new THREE.DirectionalLight(0xffffff, 1);
      key.position.set(2, 2, 4);
      const fill = new THREE.DirectionalLight(0x9bbcff, 0.6);
      fill.position.set(-3, -1, 2);
      const amb = new THREE.AmbientLight(0x9ec5fe, 0.5);
      scene.add(key, fill, amb);
      let rafId;
      const t0 = performance.now();
      const animate = () => {
        const t = (performance.now() - t0) / 1000;
        const eased = Math.min(1, t / 1.0);
        group.children.forEach((m, i) => {
          m.rotation.x += 0.002 + i * 0.001;
          m.rotation.y += 0.003 + i * 0.0008;
          m.material.opacity = 0.18 * eased;
        });
        renderer.render(scene, camera);
        rafId = requestAnimationFrame(animate);
      };
      animate();
      const onResize = () => {
        if (!mount) return;
        const w = mount.clientWidth || width;
        const h = mount.clientHeight || height;
        renderer.setSize(w, h);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
      };
      window.addEventListener('resize', onResize);
      cleanup = () => {
        cancelAnimationFrame(rafId);
        window.removeEventListener('resize', onResize);
        group.children.forEach((m) => { m.geometry.dispose(); m.material.dispose(); });
        renderer.dispose();
        if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
      };
    };
    if (!window.THREE) {
      const existing = document.querySelector('script[data-three]');
      if (existing) {
        existing.addEventListener('load', init, { once: true });
      } else {
        const s = document.createElement('script');
        s.src = 'https://unpkg.com/three@0.160.0/build/three.min.js';
        s.async = true;
        s.defer = true;
        s.setAttribute('data-three', 'true');
        s.onload = init;
        document.body.appendChild(s);
      }
    } else {
      init();
    }
    return () => cleanup();
  }, []);
  return mountRef;
}


/* ============ Content (FR/EN) ============ */
const content = {
  fr: {
    metaTitle: "QUART — Planification d’équipe simple, conforme et fiable",
    metaDesc:
      "Avec QUART, créez des plannings clairs en quelques minutes et ajustez sans repartir de zéro.",
    canonical: 'https://getquart.com/',
    nav: { features: 'Fonctionnalités', howItWorks: 'Fonctionnement', pricing: 'Tarifs', cta: 'Demander une démo' },
    popularLabel: 'Populaire',
    hero: {
      eyebrow: 'Planification d’équipe',
      title: "Gérez vos plannings en un clin d’œil",
      subtitle:
        "Créez des plannings optimisés en quelques minutes, respectez les contraintes légales et adaptez-vous facilement aux imprévus.",
      ctaPrimary: 'Demander une démo',
      ctaSecondary: 'Essayer gratuitement',
      points: [
        'Gagnez des heures chaque semaine',
        'Respects des contraines personnelles et réglementaires',
        'Ajustement de planning instantané',
      ],
    },
    value: {
      title: 'Pourquoi QUART',
      items: [
        { icon: IconClock, title: 'Premier jet simple et conforme', description: 'Obtenez une première version qui respecte un maximum de contraintes — sans formules Excel.' },
        { icon: IconUsers, title: 'Planning optimisé, capacité libérée', description: "Améliorez l’utilisation de l’équipe et limitez le recours aux ressources externes." },
        { icon: IconBolt, title: 'Imprévus gérés sans casse', description: "Ajustez le planning existant en cas d’absence ou d’événement de dernière minute." },
      ]
    },
    disruptions: {
      text: "Imprévu de dernière minute ? Réaffectez en 30 s, sans perdre vos ajustements.",
      cta: "Voir comment on gère les imprévus"
    },
    benefits: {
      title: 'Une gestion fine de vos plannings',
      items: [
        { icon: IconCalendar, title: 'Respect des contraintes réglementaires', description: 'Amplitudes, repos, nuits/week‑ends, responsabilités (France).'},
        { icon: IconUsers, title: 'Gestion des rôles et compétences', description: "Affectez les bonnes personnes aux bons créneaux." },
        { icon: IconBolt, title: 'Ajustable et transparent', description: "Itérez facilement sur le planning : générez, ajustez, regénérez sans friction." },
      ],
    },
    how: {
      title: 'Comment ça marche',
      subtitle: "En 3 étapes",
      desc: 'Du cadrage à l’itération — vous gardez la main.',
      items: [
        { title: 'Configurez vos événements et votre équipe', description: 'Créez vos programmes/créneaux et ajoutez les membres de l’équipe.' },
        { title: 'Ajoutez contraintes et disponibilités', description: "Définissez règles et préférences. Invitez vos collaborateurs pour qu’ils renseignent leurs dispos." },
        { title: 'Générez en un clic et itérez', description: 'Obtenez un planning optimisé, ajustez, regénérez si besoin.' },
      ],
    },
    
    trusted: {
      title: 'Ils nous font confiance',
      items: [
        { name: 'Rédactions' },
        { name: 'Studios' },
        { name: 'TV / Radio' },
        { name: 'Agences' },
      ]
    },
    compliance: {
      items: [
        { label: 'RGPD', desc: 'Données hébergées UE' },
        { label: 'SSO', desc: 'SAML / OAuth' },
        { label: 'Exports', desc: '.ics / CSV' },
      ]
    },
    pricing: {
      title: 'Tarifs',
      subtitle: "Commencez gratuitement. Passez à un plan payant quand c’est utile.",
      plans: [
        { name: 'Gratuit', price: '0€', period: '/mois', description: 'Pour découvrir QUART', features: ['1 calendrier', 'Jusqu’à 3 personnes', 'Jeu de contraintes simple', 'Export .ics du calendrier', 'Optimisation à la semaine'], cta: 'Commencer gratuitement', popular: false },
        { name: 'Basique', price: '9.99€', period: '/mois', description: 'Pour petites équipes', features: ['Calendriers illimités', "Jusqu'à 10 ressources par calendrier", 'Contraintes simples', 'Export .ics', 'Optimisation au mois'], cta: 'Essayer Basique', popular: false },
        { name: 'Pro', price: '99.90€', period: '/mois', description: 'Pour équipes en croissance', features: ['Calendriers illimités', 'Ressources illimitées par calendrier', 'Support email', "Personnalisation des contraintes à l’onboarding"], cta: 'Choisir Pro', popular: false },
        { name: 'Entreprise', price: 'Sur demande', period: '', description: 'Pour grandes organisations', features: ['Calendriers illimités', 'Ressources illimitées par calendrier', 'Support email', 'Jeu de contraintes personnalisé', 'Support dédié', 'SSO', 'Instances dédiées', 'Formation équipe'], cta: 'Nous contacter', popular: true },
      ],
    },
    cta: {
      title: 'Prêt à simplifier vos plannings ?',
      text: "Présentez‑nous votre contexte. En 20 minutes, nous montrons comment QUART s’adapte à vos règles et imprévus.",
      btnPrimary: 'Demander une démo',
      btnSecondary: 'Voir les offres'
    },
    
    footer: 'Tous droits réservés.'
  },
  en: {
    metaTitle: 'QUART — Simple, compliant, reliable team scheduling',
    metaDesc:
      'Build clear schedules in minutes, model French rules, and adjust without starting over.',
    canonical: 'https://getquart.com/en',
    nav: { features: 'Features', howItWorks: 'How it works', pricing: 'Pricing', cta: 'Book a demo' },
    popularLabel: 'Popular',
    hero: {
      eyebrow: 'Team scheduling',
      title: 'Manage your schedules in a snap',
      subtitle:
        'Build optimized schedules in minutes, comply with legal constraints, and easily handle last-minute changes.',
      ctaPrimary: 'Book a demo',
      ctaSecondary: 'Try for free',
      points: [
        'Save hours every week',
        'Personal and regulatory labor compliance',
        'Instant rescheduling',
      ],
    },
    value: {
      title: 'Why QUART',
      items: [
        { icon: IconClock, title: 'Simple, compliant first draft', description: 'Get a first pass that respects constraints — no spreadsheets.' },
        { icon: IconUsers, title: 'Optimized schedule, freed capacity', description: 'Improve team utilization and reduce external staffing.' },
        { icon: IconBolt, title: 'Disruptions handled gracefully', description: 'Adjust the existing schedule for sick leave or last‑minute events.' },
      ]
    },
    disruptions: {
      text: 'Last-minute change? Reallocate in 30 seconds without breaking your edits.',
      cta: "See how we handle disruptions"
    },
    benefits: {
      title: 'Fine‑grained schedule control',
      items: [
        { icon: IconCalendar, title: 'Regulatory compliance (FR)', description: 'Hours, rest, nights/weekends, responsibilities.' },
        { icon: IconUsers, title: 'Roles & skills management', description: 'Assign the right people to the right slots.' },
        { icon: IconBolt, title: 'Adjustable and transparent', description: 'Iterate easily: generate, adjust, and regenerate without friction.' },
      ],
    },
    how: {
      title: 'How it works',
      subtitle: 'In 3 steps',
      desc: 'From setup to iteration — you stay in control.',
      items: [
        { title: 'Configure events and your team', description: 'Create programs/slots and add team members.' },
        { title: 'Add constraints and availability', description: 'Define rules and preferences. Invite teammates to fill availability.' },
        { title: 'Generate in one click and iterate', description: 'Get an optimized schedule, adjust, regenerate if needed.' },
      ],
    },
    
    
    pricing: {
      title: 'Pricing',
      subtitle: 'Start free. Upgrade when it makes sense.',
      plans: [
        { name: 'Free', price: '0€', period: '/month', description: 'To discover QUART', features: ['1 calendar', 'Up to 3 people', 'Simple constraint set', '.ics calendar export', 'Weekly optimization'], cta: 'Start for free', popular: false },
        { name: 'Basic', price: '9.99€', period: '/month', description: 'For small teams', features: ['Unlimited calendars', 'Up to 10 member per calendar', 'Simple constraints', '.ics export', 'Monthly optimization'], cta: 'Try Basic', popular: false },
        { name: 'Pro', price: '99.90€', period: '/month', description: 'For growing teams', features: ['Unlimited calendars', 'Unlimited member per calendar', 'Email support', 'Constraint customization at onboarding'], cta: 'Choose Pro', popular: false },
        { name: 'Enterprise', price: 'On request', period: '', description: 'For large organizations', features: ['Unlimited calendars', 'Unlimited member per calendar', 'Email support', 'Custom constraint set', 'Dedicated support', 'SSO', 'Dedicated instances', 'Team training'], cta: 'Contact us', popular: true },
      ],
    },
    cta: {
      title: 'Ready to simplify scheduling?',
      text: 'Tell us about your context. In 20 minutes, we’ll show how QUART fits your rules and disruptions.',
      btnPrimary: 'Book a demo',
      btnSecondary: 'See plans'
    },
    
    footer: 'All rights reserved.'
  },
};

/* ============ Landing ============ */
export default function Landing() {
  const { i18n } = useTranslation('common');
  const language = (i18n.language && i18n.language.startsWith('fr')) ? 'fr' : 'en';
  const [isAuthed, setIsAuthed] = useState(!!auth.currentUser);
  const { open: openCalendly } = useCalendly();
  const heroMountRef = useThreeHero();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const t = content[language];

  useEffect(() => {
    document.title = t.metaTitle;
    setOrCreateMeta({ name: 'description' }, t.metaDesc);
    setOrCreateMeta({ property: 'og:title' }, t.metaTitle);
    setOrCreateMeta({ property: 'og:description' }, t.metaDesc);
    setOrCreateMeta({ property: 'og:type' }, 'website');
    setOrCreateMeta({ property: 'og:url' }, t.canonical);
    setOrCreateMeta({ property: 'og:image' }, 'https://getquart.com/og.jpg');
    setOrCreateMeta({ name: 'twitter:card' }, 'summary_large_image');
    setOrCreateMeta({ name: 'twitter:title' }, t.metaTitle);
    setOrCreateMeta({ name: 'twitter:description' }, t.metaDesc);
    setOrCreateMeta({ name: 'twitter:image' }, 'https://getquart.com/og.jpg');
    ensureLink('canonical', t.canonical);
    // WebSite schema
    injectJsonLd({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'QUART',
      url: t.canonical,
      description: t.metaDesc,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${t.canonical}?q={search_term_string}`,
        'query-input': 'required name=search_term_string'
      }
    });
    // FAQ schema omitted (no FAQ rendered)
  }, [t]);

  // i18next hook above triggers re-render on language changes; no custom event needed here

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setIsAuthed(!!u));
    return unsub;
  }, []);

  // Reveal-on-scroll for key sections
  useEffect(() => {
    const els = Array.from(document.querySelectorAll('.reveal'));
    if (!els.length) return;
    const onIntersect = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    };
    const obs = new IntersectionObserver(onIntersect, { threshold: 0.2, rootMargin: '0px 0px -10% 0px' });
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [language]);


  return (
    <div>
      {/* Hero with Three.js background */}
      <div className="hero-3d" ref={heroMountRef}>
      <Container size="lg" py="xl" className="gradient-hero hero-3d-content">
        <Grid align="center" gutter="xl">
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack align="flex-start" gap="md">
              {t.hero.eyebrow && (
                <Text className="section-eyebrow" c="dimmed">{t.hero.eyebrow}</Text>
              )}
              <Title order={1} className="reveal" style={{ transitionDelay: '0ms' }}>{t.hero.title}</Title>
              <Text size="lg" c="dimmed" maw={560} className="reveal" style={{ transitionDelay: '80ms' }}>{t.hero.subtitle}</Text>
              {t.hero.points && (
                <Stack gap={8} className="reveal" style={{ transitionDelay: '120ms' }}>
                  {t.hero.points.map((p, i) => (
                    <Group key={i} gap={8}>
                      <IconCircleCheck size={18} color="#2f9e44" />
                      <Text>{p}</Text>
                    </Group>
                  ))}
                </Stack>
              )}
              <Group className="reveal" style={{ transitionDelay: '160ms' }}>
                <Button className="cta-demo" size="md" onClick={openCalendly} leftSection={<IconArrowRight size={16} />}>
                  {t.hero.ctaPrimary}
                </Button>
                <Button size="md" variant="outline" component={Link} to={isAuthed ? '/account' : '/auth'}>
                  {t.hero.ctaSecondary}
                </Button>
              </Group>
              {t.hero.stats && (
                <Grid mt="md" gutter="md" w="100%" className="reveal" style={{ transitionDelay: '220ms' }}>
                  {t.hero.stats.map((s, i) => (
                    <Grid.Col key={i} span={{ base: 4 }}>
                      <Stack gap={2} align="center">
                        <Text fw={700} size="xl" c="#1c7ed6">{s.value}</Text>
                        <Text c="dimmed" size="sm" ta="center">{s.label}</Text>
                      </Stack>
                    </Grid.Col>
                  ))}
                </Grid>
              )}
            </Stack>
          </Grid.Col>
          {!isMobile && (
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Card radius="md" p="md" style={{ background: '#ffffffcc', backdropFilter: 'blur(4px)' }}>
                <div className="hero-tilt reveal" style={{ transitionDelay: '200ms' }}>
                  <div className="hero-media-wrap">
                    <img
                      className="hero-media hero-media-enter"
                      src="/hero-illu.webp"
                      alt={t.hero.title}
                      loading="eager"
                    />
                  </div>
                </div>
              </Card>
            </Grid.Col>
          )}
        </Grid>
      </Container>
      </div>

      {/* Key arguments (no header, airy layout) */}
      <Container size="lg" py="xl" style={{ scrollMarginTop: 80 }}>
        <Grid align="stretch">
          {t.value.items.map((v, i) => (
            <Grid.Col key={i} span={{ base: 12, md: 4 }}>
              <Stack gap={8} className="reveal" style={{ transitionDelay: `${100 + i * 80}ms` }}>
                <Group gap={10} align="center">
                  <v.icon className="icon-reveal" size={28} color="#1c7ed6" />
                  <Text fw={800} size="lg">{v.title}</Text>
                </Group>
                <Text c="dimmed">{v.description}</Text>
              </Stack>
            </Grid.Col>
          ))}
        </Grid>
      </Container>


      {/* Benefits (elegant layout) */}
      <Container id="features" size="lg" py="xl" style={{ scrollMarginTop: 80 }}>
        <Title ta="center" order={2} mb="lg">{t.benefits.title}</Title>
        <Grid align="stretch">
          {t.benefits.items.map((b, i) => (
            <Grid.Col key={i} span={{ base: 12, md: 4 }}>
              <Card className="feature-card reveal" radius="lg" p="lg" style={{ height: '100%', background: '#f8fbff', border: '1px solid #edf2ff', transitionDelay: `${100 + i * 80}ms` }}>
                <Group align="flex-start" gap="md" wrap="nowrap">
                  <div className="feature-icon" style={{ width: 44, height: 44, borderRadius: 999, background: '#e7f5ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 44px' }}>
                    <b.icon size={22} color="#1c7ed6" />
                  </div>
                  <Stack className="feature-content" gap={6}>
                    <Text fw={700} size="lg">{b.title}</Text>
                    <Text c="dimmed">{b.description}</Text>
                  </Stack>
                </Group>
              </Card>
            </Grid.Col>
          ))}
        </Grid>
      </Container>

      {/* How it works (timeline, distinct visual) */}
      <div className="section-alt">
        <Container id="how" size="lg" py="xl" style={{ scrollMarginTop: 80 }}>
          <Stack align="center" gap="xs" mb="lg">
            <Title order={2}>{t.how.title}</Title>
            <Text c="dimmed">{t.how.subtitle}</Text>
            <Text c="dimmed" ta="center" maw={800}>{t.how.desc}</Text>
          </Stack>
          <div className="timeline">
            {t.how.items.map((s, i) => (
              <div key={i} className="timeline-item reveal" style={{ transitionDelay: `${100 + i * 90}ms` }}>
                <div className="timeline-dot">{String(i + 1).padStart(2, '0')}</div>
                <div className="timeline-content">
                  <div className="timeline-text">
                    <Text fw={800} size="lg">{s.title}</Text>
                    <Text c="dimmed">{s.description}</Text>
                  </div>
                
                </div>
                {i < t.how.items.length - 1 && <div className="timeline-line" />}
              </div>
            ))}
          </div>
        </Container>
      </div>

      

      {/* Testimonials and FAQ intentionally omitted */}

      {/* Pricing (de-emphasized) */}
      <Container id="pricing" size="lg" py="xl" style={{ scrollMarginTop: 80 }}>
        <Stack align="center" gap="xs" mb="md">
          <Title order={2}>{t.pricing.title}</Title>
          <Text c="dimmed" ta="center" maw={700}>{t.pricing.subtitle}</Text>
        </Stack>
        <Grid gutter="md" align="stretch">
          {t.pricing.plans.map((p, i) => (
            <Grid.Col key={i} span={{ base: 12, md: 6, lg: 3 }}>
              <Card withBorder radius="md" p="lg" style={{ height: '100%', display: 'flex', flexDirection: 'column', ...(p.popular ? { borderColor: '#1c7ed6', borderWidth: 2 } : {}) }}>
                <div style={{ minHeight: 30 }}>
                  {p.popular ? (
                    <Badge color="blue" leftSection={<IconStar size={14} />} mb="sm">{content[language].popularLabel}</Badge>
                  ) : (
                    <div style={{ height: 26 }} />
                  )}
                </div>
                <Stack gap={6} style={{ flex: '0 0 auto' }}>
                  <Text fw={700}>{p.name}</Text>
                  {p.description && (<Text c="dimmed" size="sm">{p.description}</Text>)}
                </Stack>
                <Group gap={6} mt="sm" style={{ flex: '0 0 auto' }}>
                  <Title order={2}>{p.price}</Title>
                  <Text c="dimmed">{p.period}</Text>
                </Group>
                <div style={{ flex: '1 1 auto', marginTop: 10 }}>
                  <List spacing={6} icon={<IconCircleCheck size={16} color="#40c057" />}> 
                    {p.features.map((f, j) => (
                      <List.Item key={j}>
                        <Text c="dimmed">{f}</Text>
                      </List.Item>
                    ))}
                  </List>
                </div>
                <div style={{ flex: '0 0 auto', marginTop: 12 }}>
                  {p.name === 'Enterprise' || p.name === 'Entreprise' ? (
                    <Button fullWidth variant="filled" onClick={openCalendly}>{p.cta}</Button>
                  ) : (
                    <Button fullWidth variant={p.popular ? 'filled' : 'outline'} component={Link} to={isAuthed ? '/account' : '/auth'}>{p.cta}</Button>
                  )}
                </div>
              </Card>
            </Grid.Col>
          ))}
        </Grid>
      </Container>

      {/* Bottom CTA (contact-first) */}
      <Container py="xl" style={{ background: 'linear-gradient(90deg, #1971c2, #1c7ed6)' }}>
        <Stack align="center" gap="sm">
          <Title order={2} c="#fff">{t.cta.title}</Title>
          <Text c="#e7f5ff" ta="center" maw={700}>{t.cta.text}</Text>
          <Group>
            <Button className="cta-demo" color="white" variant="light" onClick={openCalendly}>
              {t.cta.btnPrimary}
            </Button>
            <Button color="white" variant="outline" component={Link} to="/pricing">
              {t.cta.btnSecondary}
            </Button>
          </Group>
        </Stack>
      </Container>

      {/* Footer */}
      <Container py="xl">
        <Group justify="space-between" align="center">
          <Group>
            <IconCalendar size={22} color="#1c7ed6" />
            <Text fw={700}>Quart</Text>
          </Group>
          <Text c="dimmed">© 2025 Quart. {content[language].footer}</Text>
        </Group>
      </Container>
    </div>
  );
}
